/**
 * Orvi Newsletter & Catalogue Integration for Shopify Theme
 * 
 * This file should be uploaded to your Shopify theme's assets folder
 * and included in theme.liquid before the closing </body> tag
 * 
 * Usage in theme.liquid:
 * {{ 'shopify-theme-integration.js' | asset_url | script_tag }}
 */

// Global submission tracking
const orviSubmissionTracker = {
  activeSubmissions: new Set(),
  isSubmitting: function (formId) {
    return this.activeSubmissions.has(formId);
  },
  startSubmission: function (formId) {
    this.activeSubmissions.add(formId);
  },
  endSubmission: function (formId) {
    this.activeSubmissions.delete(formId);
  }
};

// Orvi Newsletter Zoho CRM Integration - Simplified Version
function initializeOrviNewsletterIntegration() {
  // Find newsletter forms with multiple approaches
  let newsletterForms = [];

  // Method 1: Find forms with newsletter tag (but exclude contact forms)
  const formsWithNewsletterTag = document.querySelectorAll('form input[name="contact[tags]"][value="newsletter"]');
  formsWithNewsletterTag.forEach(input => {
    const form = input.closest('form');

    // Skip contact forms explicitly
    const isContactForm = form.id === 'ContactForm' ||
      form.id === 'contact_form_home' ||
      form.querySelector('input[name="contact[first_name]"]') ||
      form.querySelector('input[name="contact[last_name]"]') ||
      form.querySelector('input[name="contact[phone]"]') ||
      form.querySelector('input[name="contact[Form Name]"]');

    if (isContactForm) {
      return;
    }

    if (form && !newsletterForms.includes(form)) {
      newsletterForms.push(form);
    }
  });

  // Method 2: Find by form structure (if no tagged forms found)
  if (newsletterForms.length === 0) {
    const allForms = document.querySelectorAll('form');
    allForms.forEach((form, index) => {
      // Skip contact forms explicitly
      const isContactForm = form.id === 'ContactForm' ||
        form.id === 'contact_form_home' ||
        form.querySelector('input[name="contact[first_name]"]') ||
        form.querySelector('input[name="contact[last_name]"]') ||
        form.querySelector('input[name="contact[phone]"]') ||
        form.querySelector('input[name="contact[Form Name]"]');

      if (isContactForm) {
        return;
      }

      // Look for simple forms with just email input (likely newsletter)
      const emailInput = form.querySelector('input[type="email"], input[name*="email"]');
      const submitButton = form.querySelector('input[type="submit"], button[type="submit"]');
      const formInputs = form.querySelectorAll('input:not([type="hidden"])');

      // If form has email input, submit button, and not too many inputs (likely newsletter)
      if (emailInput && submitButton && formInputs.length <= 2) {
        // Check it's not a contact form (contact forms usually have name/phone fields)
        const hasNameField = form.querySelector('input[name*="name"], input[name*="first"], input[name*="last"]');
        const hasPhoneField = form.querySelector('input[name*="phone"], input[type="tel"]');

        if (!hasNameField && !hasPhoneField) {
          newsletterForms.push(form);
        }
      }
    });
  }

  // Method 3: Alternative selectors as fallback
  if (newsletterForms.length === 0) {
    const alternativeSelectors = [
      '.newsletter-form',
      '.news-l-form form',
      'form[class*="newsletter"]',
      'form[class*="subscribe"]',
      'form[id*="newsletter"]',
      'form[id*="subscribe"]'
    ];

    alternativeSelectors.forEach(selector => {
      const forms = document.querySelectorAll(selector);
      forms.forEach(form => {
        if (!newsletterForms.includes(form)) {
          newsletterForms.push(form);
        }
      });
    });
  }

  // Setup event listeners for each newsletter form
  if (newsletterForms.length > 0) {
    newsletterForms.forEach((form, index) => {
      // Generate unique form ID for tracking
      const formId = form.id || `orvi-newsletter-form-${index + 1}-${Date.now()}`;
      if (!form.id) {
        form.id = formId;
      }

      // Check if form not already handled or needs re-setup
      if (!form.hasAttribute('data-orvi-handled') || form.getAttribute('data-orvi-handled') !== 'theme-integration-script') {
        setupNewsletterForm(form, index + 1, formId);
      }
    });
  }

  // Newsletter form setup function
  function setupNewsletterForm(form, formNumber, formId) {
    // Remove any existing event listeners to prevent duplicates
    if (form._orviSubmitHandler) {
      form.removeEventListener('submit', form._orviSubmitHandler);
    }

    const submitHandler = async function (e) {
      e.preventDefault();
      e.stopImmediatePropagation(); // Prevent other handlers from executing

      // Check if already submitting
      if (orviSubmissionTracker.isSubmitting(formId)) {
        return; // Already submitting, ignore this request
      }

      // Start tracking submission
      orviSubmissionTracker.startSubmission(formId);

      // Disable form interactions during submission
      this.style.pointerEvents = 'none';

      // Check if this is actually a newsletter form (safety check)
      const newsletterTag = this.querySelector('input[name="contact[tags]"][value="newsletter"]');

      // Find email input with multiple selectors
      const emailSelectors = [
        'input[name="contact[email]"]',    // Shopify standard
        'input[type="email"]',             // Generic email input
        'input[name*="email"]',            // Name contains "email"
        'input[placeholder*="email" i]',   // Placeholder contains "email"
        'input[class*="email"]',           // Class contains "email"
        '.news-l-email'                    // Your specific class
      ];

      let email = null;
      let foundSelector = null;

      for (const selector of emailSelectors) {
        const emailInput = this.querySelector(selector);
        if (emailInput && emailInput.value.trim()) {
          email = emailInput.value.trim();
          foundSelector = selector;
          break;
        }
      }

      if (!email || !/^[^@]+@[^@]+\.[^@]+$/.test(email)) {
        alert('Please enter a valid email address.');
        orviSubmissionTracker.endSubmission(formId);
        this.style.pointerEvents = '';
        return;
      }

      try {
        await submitToNetlify(this, {
          type: 'newsletter',
          email: email,
          leadSource: 'Subscription',
          sourceByGroup: 'Subscription'
        }, 'SUBSCRIBING...', formNumber);
      } finally {
        // Always end submission tracking and re-enable form
        orviSubmissionTracker.endSubmission(formId);
        this.style.pointerEvents = '';
      }
    };

    // Store handler reference for potential removal later
    form._orviSubmitHandler = submitHandler;

    // Add event listener directly
    form.addEventListener('submit', submitHandler);
    form.setAttribute('data-orvi-handled', 'theme-integration-script');
  }

}

// Submit to Netlify function (shared between newsletter and catalogue)
async function submitToNetlify(form, data, loadingText, formNumber, formType = 'newsletter') {
  // Additional check - prevent form from being submitted again (already done in handler but as extra safety)
  form.style.pointerEvents = 'none'; // Disable all form interactions

  // Show loading state
  const submitBtn = form.querySelector('input[type="submit"], button[type="submit"]');
  const originalValue = submitBtn ? (submitBtn.value || submitBtn.textContent) : '';

  if (submitBtn) {
    if (submitBtn.tagName === 'INPUT') {
      submitBtn.value = loadingText;
    } else {
      submitBtn.textContent = loadingText;
    }
    submitBtn.disabled = true;
  }

  try {
    // Determine the correct endpoint based on form type
    const endpoints = {
      newsletter: 'https://orvizoho.netlify.app/.netlify/functions/newsletter',
      catalogue: 'https://orvizoho.netlify.app/.netlify/functions/catalogue'
    };

    const netlifyUrl = endpoints[formType] || endpoints.newsletter;

    const response = await fetch(netlifyUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify(data)
    });

    const result = await response.json();

    if (response.ok && result.success) {
      if (formType === 'catalogue') {
        if (result.duplicate) {
          alert('Your catalogue request has already been submitted!');
        } else {
          alert('Thank you! Your catalogue request has been submitted successfully.');
        }
      } else {
        if (result.duplicate) {
          alert('You are already subscribed to our newsletter!');
        } else {
          alert('Thank you for subscribing to our newsletter!');
        }
      }
      form.reset();
    } else {
      if (formType === 'catalogue') {
        alert('There was an error submitting your catalogue request. Please try again later.');
      } else {
        alert('There was an error subscribing to the newsletter. Please try again later.');
      }
    }
  } catch (err) {
    alert('Network error. Please check your connection and try again.');
  } finally {
    // Restore form and button state
    form.style.pointerEvents = ''; // Re-enable form interactions

    if (submitBtn) {
      if (submitBtn.tagName === 'INPUT') {
        submitBtn.value = originalValue;
      } else {
        submitBtn.textContent = originalValue;
      }
      submitBtn.disabled = false;
    }
  }
}

// Orvi Catalogue Zoho CRM Integration
function initializeOrviCatalogueIntegration() {
  // Find catalogue forms with multiple approaches
  let catalogueForms = [];

  // Method 1: Find specific homepage catalogue form
  const homeCatalogueForm = document.querySelector('form#home_catalogue_form');
  if (homeCatalogueForm) {
    catalogueForms.push(homeCatalogueForm);
  }

  // Method 2: Find forms with catalogue indicators
  const allForms = document.querySelectorAll('form');
  allForms.forEach(form => {
    // Skip if already found
    if (catalogueForms.includes(form)) return;

    // Look for catalogue form characteristics
    const hasCompany = form.querySelector('input[name="contact[Company]"], input[name*="company"]');
    const hasProfession = form.querySelector('select[name="contact[Profession]"], select[name*="profession"]');
    const hasCity = form.querySelector('input[name="contact[city]"], input[name*="city"]');
    const hasFormName = form.querySelector('input[value*="Catalogue"], input[value*="catalogue"]');

    if ((hasCompany && hasProfession && hasCity) || hasFormName) {
      catalogueForms.push(form);
    }
  });

  // Method 3: Find by data attributes or classes
  const catalogueSelectors = [
    'form[data-orvi-handled="catalogue-script"]',
    'form[class*="catalogue"]',
    'form[id*="catalogue"]',
    '.popup form', // Popup forms might be catalogue forms
    '.modal form'  // Modal forms might be catalogue forms
  ];

  catalogueSelectors.forEach(selector => {
    const forms = document.querySelectorAll(selector);
    forms.forEach(form => {
      // Verify this looks like a catalogue form
      const hasCompany = form.querySelector('input[name="contact[Company]"], input[name*="company"]');
      const hasProfession = form.querySelector('select[name="contact[Profession]"], select[name*="profession"]');

      if (hasCompany && hasProfession && !catalogueForms.includes(form)) {
        catalogueForms.push(form);
      }
    });
  });

  // Setup event listeners for each catalogue form
  if (catalogueForms.length > 0) {
    catalogueForms.forEach((form, index) => {
      // Generate unique form ID for tracking
      const formId = form.id || `orvi-catalogue-form-${index + 1}-${Date.now()}`;
      if (!form.id) {
        form.id = formId;
      }

      // Check if form not already handled or needs re-setup
      if (!form.hasAttribute('data-orvi-handled') || form.getAttribute('data-orvi-handled') !== 'theme-integration-catalogue') {
        setupCatalogueForm(form, index + 1, formId);
      }
    });
  }

  // Catalogue form setup function
  function setupCatalogueForm(form, formNumber, formId) {
    // Remove any existing event listeners to prevent duplicates
    if (form._orviSubmitHandler) {
      form.removeEventListener('submit', form._orviSubmitHandler);
    }

    const submitHandler = async function (e) {
      e.preventDefault();
      e.stopImmediatePropagation(); // Prevent other handlers from executing

      // Check if already submitting
      if (orviSubmissionTracker.isSubmitting(formId)) {
        return; // Already submitting, ignore this request
      }

      // Start tracking submission
      orviSubmissionTracker.startSubmission(formId);

      // Disable form interactions during submission
      this.style.pointerEvents = 'none';

      // Find form fields with multiple selectors
      const fieldMappings = findCatalogueFormFields(this);

      // Validate required fields are present (Company is optional)
      const requiredFields = ['firstName', 'lastName', 'email', 'phone', 'profession', 'city'];
      const missingFields = [];

      requiredFields.forEach(field => {
        if (!fieldMappings[field]) {
          missingFields.push(field);
        }
      });

      if (missingFields.length > 0) {
        alert('This form is missing some required fields. Please contact support.');
        orviSubmissionTracker.endSubmission(formId);
        this.style.pointerEvents = '';
        return;
      }

      // Get form data
      const formData = {
        firstName: (fieldMappings.firstName?.value || '').trim(),
        lastName: (fieldMappings.lastName?.value || '').trim(),
        email: (fieldMappings.email?.value || '').trim(),
        phone: (fieldMappings.phone?.value || '').trim(),
        company: (fieldMappings.company?.value || '').trim(),
        profession: (fieldMappings.profession?.value || '').trim(),
        city: (fieldMappings.city?.value || '').trim(),
        formName: (fieldMappings.formName?.value || '').trim(),
        leadSource: 'Catalogue Request',
        sourceByGroup: 'Catalogue Request'
      };

      // Validate form data (Company is optional - only validate if field exists)
      const emptyFields = [];
      if (!formData.firstName) emptyFields.push('First Name');
      if (!formData.lastName) emptyFields.push('Last Name');
      if (!formData.email) emptyFields.push('Email');
      if (!formData.phone) emptyFields.push('Phone');
      // Only validate Company if the field exists in the form
      if (fieldMappings.company && !formData.company) emptyFields.push('Company');
      if (!formData.profession) emptyFields.push('Profession');
      if (!formData.city) emptyFields.push('City');

      if (emptyFields.length > 0) {
        alert(`Please fill in the following required fields: ${emptyFields.join(', ')}`);
        orviSubmissionTracker.endSubmission(formId);
        this.style.pointerEvents = '';
        return;
      }

      // Email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        alert('Please enter a valid email address.');
        orviSubmissionTracker.endSubmission(formId);
        this.style.pointerEvents = '';
        return;
      }

      try {
        await submitToNetlify(this, formData, 'SENDING...', formNumber, 'catalogue');
      } finally {
        // Always end submission tracking and re-enable form
        orviSubmissionTracker.endSubmission(formId);
        this.style.pointerEvents = '';
      }
    };

    // Store handler reference for potential removal later
    form._orviSubmitHandler = submitHandler;

    // Add event listener directly
    form.addEventListener('submit', submitHandler);
    form.setAttribute('data-orvi-handled', 'theme-integration-catalogue');
  }

  // Find catalogue form fields with flexible selectors
  function findCatalogueFormFields(form) {
    const mappings = {};

    // Field selector generators
    const createSelectors = (shopifyName, genericName) => [
      `input[name="contact[${shopifyName}]"]`,
      `select[name="contact[${shopifyName}]"]`,
      `input[name="${shopifyName}"]`,
      `select[name="${shopifyName}"]`,
      `input[name*="${shopifyName.toLowerCase()}"]`,
      `select[name*="${shopifyName.toLowerCase()}"]`,
      `input[placeholder*="${genericName}" i]`,
      `select[placeholder*="${genericName}" i]`
    ];

    // Find each field
    mappings.firstName = findFieldBySelectors(form, createSelectors('first_name', 'first'));
    mappings.lastName = findFieldBySelectors(form, createSelectors('last_name', 'last'));
    mappings.email = findFieldBySelectors(form, [...createSelectors('email', 'email'), 'input[type="email"]']);
    mappings.phone = findFieldBySelectors(form, [...createSelectors('phone', 'phone'), 'input[type="tel"]']);
    mappings.company = findFieldBySelectors(form, createSelectors('Company', 'company'));
    mappings.profession = findFieldBySelectors(form, createSelectors('Profession', 'profession'));
    mappings.city = findFieldBySelectors(form, createSelectors('city', 'city'));
    mappings.formName = findFieldBySelectors(form, createSelectors('Form Name', 'form_name'));

    return mappings;
  }

  function findFieldBySelectors(form, selectors) {
    for (const selector of selectors) {
      try {
        const field = form.querySelector(selector);
        if (field) return field;
      } catch (e) {
        // Invalid selector, continue
      }
    }
    return null;
  }
}

// Setup catalogue links to open popups
function setupCatalogueLinks() {
  const catalogueSelectors = [
    'a.catalogue',
    '.catalogue a',
    'a[href*="catalogue"]',
    'button[onclick*="catalogue"]',
    '[data-toggle*="modal"][data-target*="catalogue"]'
  ];

  let foundLinks = [];
  catalogueSelectors.forEach(selector => {
    try {
      const links = document.querySelectorAll(selector);
      links.forEach(link => {
        if (!foundLinks.includes(link)) {
          foundLinks.push(link);
        }
      });
    } catch (e) {
      // Invalid selector, continue
    }
  });

  // Also check by text content
  const allLinks = document.querySelectorAll('a, button');
  allLinks.forEach(link => {
    const text = link.textContent.toLowerCase().trim();
    if ((text.includes('catalogue') || text.includes('catalog') || text.includes('request for catalogue')) &&
      !foundLinks.includes(link)) {
      foundLinks.push(link);
    }
  });

  foundLinks.forEach(link => {
    link.removeEventListener('click', catalogueLinkHandler);
    link.addEventListener('click', catalogueLinkHandler);
  });
}

function catalogueLinkHandler(e) {
  // Wait for popup/modal to appear and reinitialize catalogue forms
  setTimeout(() => {
    initializeOrviCatalogueIntegration();
  }, 500);

  // Also try with longer delays in case popup takes time to load
  setTimeout(() => {
    initializeOrviCatalogueIntegration();
  }, 1500);
}

// Initialize both newsletter and catalogue integrations
function initializeAllOrviIntegrations() {
  initializeOrviNewsletterIntegration();
  initializeOrviCatalogueIntegration();
  setupCatalogueLinks();
}

// Multiple initialization methods for Shopify compatibility
// Method 1: DOMContentLoaded
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeAllOrviIntegrations);
} else {
  // Method 2: DOM already loaded
  initializeAllOrviIntegrations();
}

// Method 3: Window load (fallback)
window.addEventListener('load', function () {
  setTimeout(initializeAllOrviIntegrations, 1000);
});

// Method 4: Shopify theme events (if available)
if (typeof Shopify !== 'undefined') {
  // Listen for section loads (Shopify theme sections)
  document.addEventListener('shopify:section:load', function (event) {
    setTimeout(initializeAllOrviIntegrations, 500);
  });
}

// Method 5: Listen for dynamic content changes (modal/popup appearances)
let observerTimeout;
const observer = new MutationObserver((mutationsList) => {
  for (const mutation of mutationsList) {
    if (mutation.type === 'childList') {
      // Check if new forms appeared (like in popups)
      const hasNewForms = document.querySelector('form:not([data-orvi-handled])');
      if (hasNewForms) {
        // Debounce the reinitialization to prevent excessive calls
        clearTimeout(observerTimeout);
        observerTimeout = setTimeout(() => {
          initializeAllOrviIntegrations();
        }, 300); // 300ms debounce delay
      }
    }
  }
});

observer.observe(document.body, {
  childList: true,
  subtree: true
});